import { lazy, useState, type ComponentType } from 'react';

type PageModule = { default: ComponentType };

export type PreloadablePage = (() => JSX.Element) & {
  /** Loads the page module so a later render is synchronous (no Suspense fallback). */
  preload: () => Promise<PageModule>;
};

/**
 * React.lazy plus a preload hook. Once the module has been preloaded (server prerender, or the
 * client before hydrateRoot) the page renders synchronously, so the first client render matches
 * the prerendered HTML instead of suspending. Each mounted instance keeps the implementation it
 * started with, so a late-resolving preload never remounts a live page.
 */
export function lazyPage(loader: () => Promise<PageModule>): PreloadablePage {
  let resolved: ComponentType | undefined;
  const Lazy = lazy(() => loader().then((mod) => { resolved = mod.default; return mod; }));

  function Page(): JSX.Element {
    const [Impl] = useState<ComponentType>(() => resolved ?? Lazy);
    return <Impl />;
  }
  Page.preload = async (): Promise<PageModule> => {
    const mod = await loader();
    resolved = mod.default;
    return mod;
  };
  return Page;
}
